import { useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { pictogramFileError } from "../lib/pictogramUpload";
import { CompactPictogramTile } from "./CompactPictogramTile";
import { GlobalPictogramEditor } from "./GlobalPictogramEditor";

const sendIcon = async (id, file) => {
  const error = pictogramFileError(file);
  if (error) throw new Error(error);
  const body = new FormData(); body.append("file", file);
  await api.post(`/admin/pictograms/${id}/icon`, body);
};

export const GlobalPictogramLibrary = ({ categories, items, reload }) => {
  const [editing, setEditing] = useState(null);
  const [confirmOnOpen, setConfirmOnOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const triggerRef = useRef(null);
  const restoreFocus = () => {
    const target = triggerRef.current?.isConnected ? triggerRef.current :
      document.querySelector('[data-testid="superadmin-pictogram-upload-button"]');
    target?.focus();
  };
  const close = () => { setEditing(null); setTimeout(restoreFocus, 0); };
  const open = (picto, event, confirmDelete = false) => { triggerRef.current = event.currentTarget; setError("");
    setConfirmOnOpen(confirmDelete);
    setEditing({ ...picto, color_hex: picto.default_color_hex || null, file: null }); };
  const create = event => { triggerRef.current = event.currentTarget; setError(""); setConfirmOnOpen(false); setEditing({ name: "", category_id: categories[0]?.id || "",
    order_index: items.length, color_hex: null, file: null }); };
  const save = async () => {
    if (!editing.name.trim() || !editing.category_id) return toast.error("Nome e categoria sono obbligatori.");
    if (!editing.id && !editing.file) return toast.error("Seleziona un file SVG o PNG.");
    if (editing.file) {
      const error = pictogramFileError(editing.file);
      if (error) return toast.error(error);
    }
    setBusy(true); setError("");
    let created = false;
    try {
      const payload = { name: editing.name.trim(), category_id: editing.category_id,
        order_index: Number(editing.order_index) || 0, color_hex: editing.color_hex };
      const id = editing.id || (await api.post("/admin/pictograms", payload)).data.id;
      created = !editing.id;
      if (created) setEditing(prev => ({ ...prev, id }));
      if (editing.id) await api.put(`/admin/pictograms/${id}`, payload);
      if (editing.file) await sendIcon(id, editing.file);
      await reload(); close(); toast.success("Pittogramma salvato");
    } catch (err) {
      await reload();
      const message = created ? "Voce creata, ma l'icona non è stata caricata. Riprova dalla scheda." :
        (err.response?.data?.detail || err.message || "Impossibile salvare il pittogramma");
      setError(message); toast.error(message);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/admin/pictograms/${editing.id}`);
      await reload(); close(); toast.success("Pittogramma eliminato");
    } catch (err) {
      setError(err.response?.data?.detail || "Pittogramma non eliminabile");
    } finally { setBusy(false); }
  };
  const groups = categories.map(c => ({ ...c, items: items.filter(p => p.category_id === c.id) }));

  return <section data-testid="admin-pictogram-library">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#D4D4D4] pb-4 mb-5">
      <h2 className="font-serif text-2xl">Pittogrammi di default</h2>
      <button type="button" className="btn-primary inline-flex items-center gap-2" disabled={!categories.length}
        onClick={create} data-testid="superadmin-pictogram-upload-button"><Plus className="w-4 h-4" />Nuovo pittogramma</button>
    </div>
    {items.length === 0 && <p className="text-sm text-[#707070] py-6" data-testid="admin-pictograms-empty">Nessun pittogramma. Aggiungi il primo simbolo nella categoria desiderata.</p>}
    {groups.filter(g => g.items.length).map(g => <div key={g.id} className="mb-8" data-testid={`admin-picto-group-${g.id}`}>
      <h3 className="font-serif text-xl mb-4" data-testid={`admin-picto-group-title-${g.id}`}>{g.name}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
        {g.items.map(p => <div key={p.id} className="relative min-w-0">
          <CompactPictogramTile pictogram={p} onOpen={event => open(p, event)}
            testId={`admin-picto-card-${p.id}`} imageTestId={`admin-picto-image-${p.id}`}
            nameTestId={`admin-picto-name-${p.id}`} />
          <button type="button" onClick={event => open(p, event, true)}
            className="absolute top-1 right-1 z-10 flex h-9 w-9 items-center justify-center border border-[#D4D4D4] bg-white text-[#8C3A3A] transition-colors hover:bg-[#F4EEEE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8C3A3A]"
            aria-label={`Elimina ${p.name}`} title={`Elimina ${p.name}`}
            data-testid={`admin-picto-tile-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>)}
      </div>
    </div>)}
    {editing && <GlobalPictogramEditor draft={editing} categories={categories} busy={busy} error={error}
      confirmOnOpen={confirmOnOpen}
      onChange={setEditing} onSave={save} onDelete={remove} onClose={() => { if (!busy) close(); }}
      onReturnFocus={restoreFocus} />}
  </section>;
};